/**
 * Payer Connections — canned copy.
 *
 * Verbatim strings from the PRD (AC-5, AC-6, AC-10, AC-11, AC-14, AC-15,
 * AC-16) and from the sprout assignment's rulings. Never hardcode
 * "patient"/"client" here — none of this copy is subject-language dependent.
 */

import type { BlockedParty, EnrollmentStatus } from './types';

/** AC-6: canned "what's happening" copy per status, used when Stedi returns no status_message/rejection_reason. */
export const CANNED_STATUS_COPY: Record<string, string> = {
  DRAFT: 'We are preparing this enrollment from your intake. Nothing needed from you yet.',
  CANCELED: 'This enrollment was canceled. We are re-filing it.',
  STEDI_ACTION_REQUIRED:
    'Submitted to the clearinghouse. Waiting on the clearinghouse to start processing.',
  PROVISIONING: 'The payer is processing the enrollment.',
  PROVIDER_ACTION_REQUIRED: 'The payer needs something from you — see the instructions in Stedi.',
  REJECTED: 'The payer rejected the enrollment. Check the details and resubmit.',
  /** LIVE with no claims yet (i.e. `ready`, not yet `live`). */
  LIVE: 'Connected. Send a claim to this payer to go live.',
};

/** Fallback for a status Stedi returns that isn't in the table above (folds to `waiting` per AC-3). */
export const UNKNOWN_STATUS_COPY = 'Waiting on the payer.';

/** Resolve AC-6 copy for one rail: verbatim override first, else canned copy by status. */
export function resolveWhatsHappening(
  status: EnrollmentStatus,
  statusMessage?: string | null,
  rejectionReason?: string | null,
): string {
  if (statusMessage) return statusMessage;
  if (rejectionReason) return rejectionReason;
  return CANNED_STATUS_COPY[status] ?? UNKNOWN_STATUS_COPY;
}

/** Pluralize "payer" — the only noun this feature counts. */
export function pluralizePayers(count: number): string {
  return count === 1 ? 'payer' : 'payers';
}

/** AC-5: headline, by priority — needs_customer, else ready, else neither. */
export function headline(counts: { needs_customer: number; ready: number }): string {
  if (counts.needs_customer > 0) {
    return `${counts.needs_customer} ${pluralizePayers(counts.needs_customer)} waiting on you`;
  }
  if (counts.ready > 0) {
    return `${counts.ready} ${pluralizePayers(counts.ready)} ready for a first claim`;
  }
  return 'Nothing is waiting on you';
}

/** AC-5 subhead: "N payers enrolling · N live and handed to the claim workbench". */
export function subhead(enrollingCount: number, liveCount: number): string {
  return `${enrollingCount} ${pluralizePayers(enrollingCount)} enrolling · ${liveCount} live and handed to the claim workbench`;
}

/** AC-4 footer, left side. */
export function footer(liveCount: number): string {
  return liveCount > 0
    ? `${liveCount} ${pluralizePayers(liveCount)} live — managed in the claim workbench`
    : 'No payers live yet';
}

/** AC-10 fine print, verbatim. */
export const FINE_PRINT =
  "This covers the practice's connection to each payer. It does not cover whether each clinician is credentialed.";

/** AC-11: zero enrollment rows. */
export const EMPTY_STATE_COPY =
  'JotPsych is setting up your payers. Nothing is waiting on you yet.';

/**
 * The passthrough failed. Not an acceptance criterion — AC-11 covers the
 * empty organization, not an unreachable clearinghouse — but the section
 * has to say something, and the PRD's rule is no snackbar spam: an inline
 * message with a Retry that refetches.
 */
export const LOAD_ERROR_COPY = 'We could not reach the clearinghouse just now.';

/** Label on the inline error's refetch control. */
export const RETRY_LABEL = 'Retry';

/** AC-16 roster chip-strip line: "N payers · N live". */
export function rosterPayerLine(payerCount: number, liveCount: number): string {
  return `${payerCount} ${pluralizePayers(payerCount)} · ${liveCount} live`;
}

/** AC-14 "Blocked on" chip labels. */
export const BLOCKED_ON_LABELS: Record<BlockedParty, string> = {
  JOTPSYCH: 'On JotPsych',
  CUSTOMER: 'Needs you',
  PAYER: 'Waiting on payer',
  NONE: 'Nothing outstanding',
};

/** AC-15 tooltip for a null days-blocked value. */
export const NO_TIMESTAMP_TOOLTIP = 'No timestamp on this org to measure from';
